import { type FC } from "react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { ClosableModal, ClosableModalBody, ClosableModalHeader } from "../../../shared/components";
import { type ModalProps } from "../../../shared/hooks";
import { TextLink } from "../../../shared/styles";
import { useLensDistributionRestrictionsDeduped } from "../../hooks/useLensDistributionRestrictions";
import { DISTRIBUTIONS_DOCS_LINK } from "../constants";

import { BoldenText, List, ListItem, MessagesText } from "./LensDistributionRestrictionsModal.styles";
import {
    DistributionRestrictionsClosingMessage,
    DistributionRestrictionsDocsLinkText,
    DistributionRestrictionsGeneralMessage,
    DistributionRestrictionsHeader,
    DistributionRestrictionsOpeningMessage,
} from "./messages.generated";

interface LensDistributionRestrictionsModalProps {
    modalProps: ModalProps;
    lensName: string;
    lensId: string;
}

export const LensDistributionRestrictionsModal: FC<LensDistributionRestrictionsModalProps> = ({
    modalProps,
    lensName,
    lensId,
}) => {
    const { messagesDeduped } = useLensDistributionRestrictionsDeduped(lensId);

    const body =
        messagesDeduped.length > 0 ? (
            <MessagesText>
                <p>
                    <DistributionRestrictionsOpeningMessage lensName={<BoldenText>{lensName}</BoldenText>} />
                </p>
                <List>
                    {messagesDeduped.map((message, index) => (
                        <ListItem key={index}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    a: ({ href, children }) => (
                                        <TextLink target="_blank" href={href} rel="noreferrer">
                                            {children}
                                        </TextLink>
                                    ),
                                }}
                            >
                                {message}
                            </ReactMarkdown>
                        </ListItem>
                    ))}
                </List>
                <p>
                    <DistributionRestrictionsClosingMessage
                        docsLink={
                            <TextLink href={DISTRIBUTIONS_DOCS_LINK} target="_blank" rel="noopener noreferrer">
                                <DistributionRestrictionsDocsLinkText />
                            </TextLink>
                        }
                    />
                </p>
            </MessagesText>
        ) : (
            <DistributionRestrictionsGeneralMessage
                docsLink={
                    <TextLink href={DISTRIBUTIONS_DOCS_LINK} target="_blank" rel="noopener noreferrer">
                        <DistributionRestrictionsDocsLinkText />
                    </TextLink>
                }
            />
        );
    return (
        <ClosableModal modalProps={modalProps}>
            <ClosableModalHeader>
                <DistributionRestrictionsHeader />
            </ClosableModalHeader>
            <ClosableModalBody>{body}</ClosableModalBody>
        </ClosableModal>
    );
};
